import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { API_URL } from './utils/authUtils';
import { devLogSafe, devLog, devError } from './utils/secureLogger';

const BACKEND_URL = API_URL;

const PartnerVerificationForm = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form data
  const [verificationData, setVerificationData] = useState(null);
  const [studentData, setStudentData] = useState({});

  // Feature toggle state
  const [raceRequired, setRaceRequired] = useState(false);
  
  // View and pagination state
  const [viewMode, setViewMode] = useState('verify-all'); // 'verify-all' or 'paginated'
  const [currentStudentIndex, setCurrentStudentIndex] = useState(0);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredStudents, setFilteredStudents] = useState([]);

  // Dropdown options
  const scholarStatusOptions = ['Graduated', 'Continuing', 'Health Leave of Absence', 'Co-Op Leave of Absence', 'Temporary Leave Status', 'Disqualification Withdrawal', 'University Withdrawal', 'External Transfer', 'Permanent Leave'];
  const classificationOptions = ['College - 1st Year', 'College - 2nd Year', 'College - 3rd Year', 'College - 4th Year', 'College - 5th Year', 'N/A'];
  const citizenshipOptions = ['US CITIZEN', 'NON US CITIZEN (PERMANENT RESIDENT)', 'NON US CITIZEN (PERMIT)', 'NON US CITIZEN (OTHER)'];
  const genderOptions = ['Male', 'Female', 'Nonconforming', 'Transgender Male', 'Transgender Female', 'Non-Binary', 'NonDisclose'];
  const raceOptions = ['AA/BLACK', 'AL/AlaskaNat', 'ASIAN', 'LATINx/o/a', 'NonDisclose', 'PI/NatHawaii', 'WHITE', 'MULTI-RACIAL'];

  useEffect(() => {
    if (!token) {
      setError('No verification token provided');
      setLoading(false);
      return;
    }
    fetchRaceRequiredSetting();
    fetchVerificationData();
  }, [token]);

  // Fetch race required setting from admin settings
  const fetchRaceRequiredSetting = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/settings/partner_verification_race_required/public`);
      setRaceRequired(response.data.setting_value === 'true');
    } catch (err) {
      console.error('Error fetching race required setting:', err);
      // Default to false on error (data minimization)
      setRaceRequired(false);
    }
  };

  const fetchVerificationData = async () => {
    try {
      // Include partner token in headers if available (for dashboard access)
      const partnerToken = localStorage.getItem('partnerToken');
      const headers = partnerToken ? { 'Authorization': `Bearer ${partnerToken}` } : {};
      
      const response = await axios.get(`${BACKEND_URL}/api/partner/verify/${token}`, { headers });
      
      // Sort students: Priority first (created_at != 'default' AND partner_has_verified_status = 'pending'), then alphabetically
      const sortedStudents = [...response.data.students].sort((a, b) => {
        const aPriority = a.created_at !== 'default' && a.partner_has_verified_status === 'pending';
        const bPriority = b.created_at !== 'default' && b.partner_has_verified_status === 'pending';
        
        // Priority students first
        if (aPriority && !bPriority) return -1;
        if (!aPriority && bPriority) return 1;
        
        // Then alphabetical within each group
        return a.name.localeCompare(b.name);
      });
      
      // Debug: Log the first few students to check data structure
      devLogSafe('First student data', sortedStudents[0]);
      devLog('Students with priority:', sortedStudents.filter(s => 
        s.created_at !== 'default' && s.partner_has_verified_status === 'pending'
      ));
      
      setVerificationData({
        ...response.data,
        students: sortedStudents
      });
      
      // Initialize student data - only pre-populate name, everything else starts empty
      const initialStudentData = {};
      sortedStudents.forEach(student => {
        initialStudentData[student.id] = {
          student_id: student.id,
          first_name_verified_sp25: student.existing_verification.first_name_verified_sp25 || '',
          last_name_verified_sp25: student.existing_verification.last_name_verified_sp25 || '',
          block_grant_scholar_verified_sp25: student.existing_verification.block_grant_scholar_verified_sp25 || '',
          corporate_scholar_verified_sp25: student.existing_verification.corporate_scholar_verified_sp25 || '',
          special_scholarship_verified_sp25: student.existing_verification.special_scholarship_verified_sp25 || '',
          nvb_scholar_verified_sp25: student.existing_verification.nvb_scholar_verified_sp25 || '',
          university_email_verified: student.existing_verification.university_email_verified || '',
          applicant_status_verified_sp25: student.existing_verification.applicant_status_verified_sp25 || '',
          date_of_initial_block_term_verified_sp25: student.existing_verification.date_of_initial_block_term_verified_sp25 || '',
          matriculation_date_verified_sp25: student.existing_verification.matriculation_date_verified_sp25 || '',
          conferred_grad_date_verified_sp25: student.existing_verification.conferred_grad_date_verified_sp25 || '',
          cumulative_gpa_at_graduation_verified_sp25: student.existing_verification.cumulative_gpa_at_graduation_verified_sp25 || '',
          gpa_verified: student.existing_verification.gpa_verified || '',
          expected_grad_date_verified_sp25: student.existing_verification.expected_grad_date_verified_sp25 || '',
          major_at_matriculation_verified_sp25: student.existing_verification.major_at_matriculation_verified_sp25 || '',
          major_verified: student.existing_verification.major_verified || '',
          current_academic_class_verified: student.existing_verification.current_academic_class_verified || '',
          citizenship_status_verified_sp25: student.existing_verification.citizenship_status_verified_sp25 || '',
          gender_verified: student.existing_verification.gender_verified || '',
          race_verified: student.existing_verification.race_verified || ''
        };
      });
      setStudentData(initialStudentData);
      setFilteredStudents(sortedStudents);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching verification data:', err);
      setError(err.response?.data?.detail || 'Failed to load verification data');
      setLoading(false);
    }
  };

  const updateStudentField = (studentId, field, value) => {
    setStudentData(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        [field]: value
      }
    }));
  };

  // Search functionality
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    if (!verificationData) return;
    
    if (query.trim() === '') {
      setFilteredStudents(verificationData.students);
    } else {
      const filtered = verificationData.students.filter(student =>
        student.name?.toLowerCase().includes(query.toLowerCase()) ||
        (student.email && student.email.toLowerCase().includes(query.toLowerCase()))
      );
      
      // Apply the same priority sorting to filtered results
      const sortedFiltered = filtered.sort((a, b) => {
        const aPriority = a.created_at !== 'default' && a.partner_has_verified_status === 'pending';
        const bPriority = b.created_at !== 'default' && b.partner_has_verified_status === 'pending';
        
        // Priority students first
        if (aPriority && !bPriority) return -1;
        if (!aPriority && bPriority) return 1;
        
        // Then alphabetical within each group
        return a.name.localeCompare(b.name);
      });
      
      setFilteredStudents(sortedFiltered);
    }
    
    // Reset pagination if in paginated view
    if (viewMode === 'paginated') {
      setCurrentStudentIndex(0);
    }
  };

  const validateForm = (studentsToSubmit) => {
    const errors = [];
    
    studentsToSubmit.forEach(student => {
      const data = studentData[student.id];
      
      // Skip validation for already verified students
      if (student.existing_verification.is_verified) {
        return;
      }
      
      // Required fields validation
      const requiredFields = [
        'first_name_verified_sp25',
        'last_name_verified_sp25',
        'block_grant_scholar_verified_sp25',
        'corporate_scholar_verified_sp25',
        'special_scholarship_verified_sp25',
        'nvb_scholar_verified_sp25',
        'university_email_verified',
        'applicant_status_verified_sp25',
        'date_of_initial_block_term_verified_sp25',
        'matriculation_date_verified_sp25',
        'gpa_verified',
        'expected_grad_date_verified_sp25',
        'major_at_matriculation_verified_sp25',
        'major_verified',
        'current_academic_class_verified',
        'citizenship_status_verified_sp25',
        'gender_verified'
      ];

      // Only add race_verified if race is required
      if (raceRequired) {
        requiredFields.push('race_verified');
      }

      requiredFields.forEach(field => {
        if (!data[field] || data[field].toString().trim() === '') {
          errors.push(`${student.name}: ${field.replace(/_/g, ' ')} is required`);
        }
      });

      // Conditional validation for cumulative GPA if graduated
      if (data.conferred_grad_date_verified_sp25 && (!data.cumulative_gpa_at_graduation_verified_sp25 || data.cumulative_gpa_at_graduation_verified_sp25.toString().trim() === '')) {
        errors.push(`${student.name}: Final Cumulative GPA is required when a graduation date is provided`);
      }
    });

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Only submit students that haven't been verified yet
    const unverifiedStudents = verificationData.students.filter(
      student => !student.existing_verification.is_verified
    );
    
    if (unverifiedStudents.length === 0) {
      setError('All students have already been verified.');
      return;
    }
    
    const validationErrors = validateForm(unverifiedStudents);
    if (validationErrors.length > 0) {
      setError('Please fix the following errors:\n' + validationErrors.join('\n'));
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const submission = {
        token: token,
        students: unverifiedStudents.map(student => ({
          ...studentData[student.id],
          // Convert date strings to proper format
          date_of_initial_block_term_verified_sp25: studentData[student.id].date_of_initial_block_term_verified_sp25 || null,
          matriculation_date_verified_sp25: studentData[student.id].matriculation_date_verified_sp25 || null,
          conferred_grad_date_verified_sp25: studentData[student.id].conferred_grad_date_verified_sp25 || null,
          expected_grad_date_verified_sp25: studentData[student.id].expected_grad_date_verified_sp25 || null,
          cumulative_gpa_at_graduation_verified_sp25: studentData[student.id].cumulative_gpa_at_graduation_verified_sp25 ? parseFloat(studentData[student.id].cumulative_gpa_at_graduation_verified_sp25) : null
        }))
      };

      // Include partner token in headers if available (for dashboard access)
      const partnerToken = localStorage.getItem('partnerToken');
      const headers = partnerToken ? { 'Authorization': `Bearer ${partnerToken}` } : {};

      const response = await axios.post(`${BACKEND_URL}/api/partner/verify/submit`, submission, { headers });
      
      if (response.data.all_students_completed) {
        setSuccess(true);
      } else {
        // Partial submission successful - reload the form to show updated status
        setError({
          type: 'success',
          message: `Successfully verified ${response.data.students_verified} student(s). ${response.data.remaining_students} student(s) remaining. You can continue verifying the remaining students using this same link.`
        });
        // Reload the verification data to show updated status
        fetchVerificationData();
      }
    } catch (err) {
      console.error('Error submitting verification:', err);
      setError(err.response?.data?.detail || 'Failed to submit verification');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSingleStudentSubmit = async (e) => {
    e.preventDefault();
    
    const currentStudent = filteredStudents[currentStudentIndex];
    
    // Check if this student is already verified
    if (currentStudent.existing_verification.is_verified) {
      setError('This student has already been verified.');
      return;
    }
    
    // Validate only the current student
    const validationErrors = validateForm([currentStudent]);
    if (validationErrors.length > 0) {
      setError('Please fix the following errors:\n' + validationErrors.join('\n'));
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const submission = {
        token: token,
        students: [{
          ...studentData[currentStudent.id],
          // Convert date strings to proper format
          date_of_initial_block_term_verified_sp25: studentData[currentStudent.id].date_of_initial_block_term_verified_sp25 || null,
          matriculation_date_verified_sp25: studentData[currentStudent.id].matriculation_date_verified_sp25 || null,
          conferred_grad_date_verified_sp25: studentData[currentStudent.id].conferred_grad_date_verified_sp25 || null,
          expected_grad_date_verified_sp25: studentData[currentStudent.id].expected_grad_date_verified_sp25 || null,
          cumulative_gpa_at_graduation_verified_sp25: studentData[currentStudent.id].cumulative_gpa_at_graduation_verified_sp25 ? parseFloat(studentData[currentStudent.id].cumulative_gpa_at_graduation_verified_sp25) : null
        }]
      };

      // Include partner token in headers if available (for dashboard access)
      const partnerToken = localStorage.getItem('partnerToken');
      const headers = partnerToken ? { 'Authorization': `Bearer ${partnerToken}` } : {};

      const response = await axios.post(`${BACKEND_URL}/api/partner/verify/submit`, submission, { headers });
      
      // Show success message and reload data
      setError({
        type: 'success',
        message: `Successfully verified ${currentStudent.name}. ${response.data.remaining_students > 0 ? `${response.data.remaining_students} student(s) remaining.` : 'All students completed!'}`
      });
      
      // Reload the verification data to show updated status
      fetchVerificationData();
      
    } catch (err) {
      console.error('Error submitting verification:', err);
      setError(err.response?.data?.detail || 'Failed to submit verification');
    } finally {
      setSubmitting(false);
    }
  };

  const nextStudent = () => {
    if (currentStudentIndex < filteredStudents.length - 1) {
      setCurrentStudentIndex(currentStudentIndex + 1);
      setError(null);
    }
  };

  const prevStudent = () => {
    if (currentStudentIndex > 0) {
      setCurrentStudentIndex(currentStudentIndex - 1);
      setError(null);
    }
  };

  const goToStudent = (index) => {
    setCurrentStudentIndex(index);
    setError(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading verification form...</p>
        </div>
      </div>
    );
  }

  if (error && !verificationData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
              <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.88-.833-2.5 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="mt-2 text-lg font-medium text-gray-900">Verification Error</h3>
            <p className="mt-1 text-sm text-gray-500">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
              <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="mt-2 text-lg font-medium text-gray-900">Verification Submitted</h3>
            <p className="mt-1 text-sm text-gray-500">
              Thank you for completing the student verification. The information has been successfully submitted.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="text-center">
            <img
              src="/nacme_logo.jpg"
              alt="NACME Logo"
              className="h-16 w-auto mx-auto mb-4"
            />
            <h1 className="text-2xl font-bold text-gray-900">University Partner Verification Form</h1>
            <div className="mt-4 text-sm text-gray-600">
              <p><strong>University:</strong> {verificationData.university_partner.university}</p>
              <p><strong>Partner:</strong> {verificationData.university_partner.full_name}</p>
              <p><strong>Students to Verify:</strong> {verificationData.students.length}</p>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-lg font-semibold text-gray-900">Verification Progress</h3>
              <span className="text-sm text-gray-600">
                {verificationData.students.filter(s => s.existing_verification.is_verified).length} of {verificationData.students.length} verified
              </span>
            </div>
            
            {(() => {
              const totalStudents = verificationData.students.length;
              const verifiedStudents = verificationData.students.filter(s => s.existing_verification.is_verified).length;
              const priorityStudents = verificationData.students.filter(s => 
                s.created_at !== 'default' && s.partner_has_verified_status === 'pending'
              ).length;
              const progressPercentage = totalStudents > 0 ? Math.round((verifiedStudents / totalStudents) * 100) : 0;
              
              return (
                <>
                  <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                    <div 
                      className="bg-gradient-to-r from-green-500 to-green-600 h-3 rounded-full transition-all duration-500 ease-out"
                      style={{ width: `${progressPercentage}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <div className="flex flex-col">
                      <span className="text-gray-600">
                        {progressPercentage}% Complete
                      </span>
                      {priorityStudents > 0 && (
                        <span className="text-red-600 text-xs mt-1 flex items-center">
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          {priorityStudents} priority student{priorityStudents !== 1 ? 's' : ''} pending
                        </span>
                      )}
                    </div>
                    <span className={`font-medium ${
                      progressPercentage === 100 
                        ? 'text-green-600' 
                        : progressPercentage >= 50 
                        ? 'text-blue-600' 
                        : 'text-orange-600'
                    }`}>
                      {progressPercentage === 100 
                        ? '🎉 All students verified!' 
                        : `${totalStudents - verifiedStudents} remaining`}
                    </span>
                  </div>
                </>
              );
            })()}
          </div>
        </div>

        {/* Search and View Toggle */}
        <div className="bg-white rounded-lg shadow-lg p-4 mb-6">
          {/* Search Bar */}
          <div className="mb-4">
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-2">
              Search Students
            </label>
            <div className="relative">
              <input
                type="text"
                id="search"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Search by name or email..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pl-10"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
            {searchQuery && (
              <p className="mt-1 text-sm text-gray-600">
                Showing {filteredStudents.length} of {verificationData.students.length} students
              </p>
            )}
          </div>
          
          {/* View Toggle */}
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Verification Mode</h3>
            <div className="flex bg-gray-200 rounded-lg p-1">
              <button
                type="button"
                onClick={() => setViewMode('verify-all')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'verify-all'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-700 hover:text-gray-900'
                }`}
              >
                Verify All ({filteredStudents.filter(s => !s.existing_verification.is_verified).length} unverified)
              </button>
              <button
                type="button"
                onClick={() => setViewMode('paginated')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'paginated'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-700 hover:text-gray-900'
                }`}
              >
                One at a Time
              </button>
            </div>
          </div>
          <p className="text-sm text-gray-600 mt-2">
            {viewMode === 'verify-all' 
              ? 'Complete verification for all students and submit together'
              : 'Work through students individually and submit each verification separately'
            }
          </p>
        </div>

        {/* Error/Success Display */}
        {error && (
          <div className={`px-4 py-3 rounded mb-6 ${
            typeof error === 'object' && error.type === 'success' 
              ? 'bg-green-100 border border-green-400 text-green-700'
              : 'bg-red-100 border border-red-400 text-red-700'
          }`}>
            <pre className="whitespace-pre-wrap text-sm">
              {typeof error === 'object' ? error.message : error}
            </pre>
          </div>
        )}

        {/* No results message */}
        {filteredStudents.length === 0 && searchQuery && (
          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No students found</h3>
            <p className="mt-1 text-sm text-gray-500">No students match your search "{searchQuery}"</p>
          </div>
        )}

        {/* Verify All View */}
        {viewMode === 'verify-all' && filteredStudents.length > 0 && (
          <form onSubmit={handleSubmit}>
          {filteredStudents.map((student, index) => (
            <div key={student.id} className={`rounded-lg shadow-lg p-6 mb-6 ${
              student.existing_verification.is_verified 
                ? 'bg-green-50 border-2 border-green-200' 
                : 'bg-white'
            }`}>
              <div className="flex justify-between items-start mb-6">
                <div className="flex items-center">
                  {/* Priority flag */}
                  {student.created_at !== 'default' && student.partner_has_verified_status === 'pending' && (
                    <svg className="w-5 h-5 text-red-600 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  )}
                  <h2 className="text-xl font-semibold text-gray-900">
                    Student {index + 1}: {student.name}
                  </h2>
                </div>
                <div className="flex flex-col items-end gap-2">
                  {/* Priority application info */}
                  {student.created_at !== 'default' && student.partner_has_verified_status === 'pending' && (
                    <div className="text-right">
                      <p className="text-sm font-medium text-red-600">Applied for NACME Corporate Scholarship:</p>
                      <p className="text-sm text-red-500">{new Date(student.created_at).toLocaleDateString()}</p>
                    </div>
                  )}
                  {/* Verified status */}
                  {student.existing_verification.is_verified && (
                    <div className="flex items-center text-green-600">
                      <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="font-medium">Verified</span>
                    </div>
                  )}
                </div>
              </div>
              
              {student.existing_verification.is_verified && (
                <div className="mb-4 p-3 bg-green-100 border border-green-300 rounded">
                  <p className="text-green-800 text-sm">
                    ✓ This student has already been verified. You can skip to the next student.
                  </p>
                </div>
              )}
              
              <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 ${
                student.existing_verification.is_verified ? 'opacity-50 pointer-events-none' : ''
              }`}>
                {/* Field 1: Name (read-only) */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    1. Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={student.name}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600"
                  />
                </div>

                {/* Field 2: First Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    2. First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={studentData[student.id]?.first_name_verified_sp25 || ''}
                    onChange={(e) => updateStudentField(student.id, 'first_name_verified_sp25', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                {/* Field 3: Last Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    3. Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={studentData[student.id]?.last_name_verified_sp25 || ''}
                    onChange={(e) => updateStudentField(student.id, 'last_name_verified_sp25', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                {/* Field 4: NACME Block Grant Scholarship */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    4. NACME Block Grant Scholarship Recipient? <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={studentData[student.id]?.block_grant_scholar_verified_sp25 || ''}
                    onChange={(e) => updateStudentField(student.id, 'block_grant_scholar_verified_sp25', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select...</option>
                    <option value="Y">Y</option>
                    <option value="N">N</option>
                  </select>
                </div>

                {/* Field 5: NACME Corporate Scholarship */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    5. NACME Corporate Scholarship Recipient? <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={studentData[student.id]?.corporate_scholar_verified_sp25 || ''}
                    onChange={(e) => updateStudentField(student.id, 'corporate_scholar_verified_sp25', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select...</option>
                    <option value="Y">Y</option>
                    <option value="N">N</option>
                  </select>
                </div>

                {/* Field 6: NACME Special Scholarship */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    6. NACME Special Scholarship Recipient? <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={studentData[student.id]?.special_scholarship_verified_sp25 || ''}
                    onChange={(e) => updateStudentField(student.id, 'special_scholarship_verified_sp25', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select...</option>
                    <option value="Y">Y</option>
                    <option value="N">N</option>
                  </select>
                </div>

                {/* Field 7: NACME NVB Scholarship */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    7. NACME National Virtual Bridge (NVB) Scholarship Recipient? <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={studentData[student.id]?.nvb_scholar_verified_sp25 || ''}
                    onChange={(e) => updateStudentField(student.id, 'nvb_scholar_verified_sp25', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select...</option>
                    <option value="Y">Y</option>
                    <option value="N">N</option>
                  </select>
                </div>

                {/* Field 8: University Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    8. Enter University Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={studentData[student.id]?.university_email_verified || ''}
                    onChange={(e) => updateStudentField(student.id, 'university_email_verified', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                {/* Field 9: Scholar Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    9. Scholar Status (Fall 2025) <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={studentData[student.id]?.applicant_status_verified_sp25 || ''}
                    onChange={(e) => updateStudentField(student.id, 'applicant_status_verified_sp25', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select...</option>
                    {scholarStatusOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>

                {/* Field 10: Date of Initial NACME Block Scholarship */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    10. Date/Term of Initial NACME Block Scholarship <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={studentData[student.id]?.date_of_initial_block_term_verified_sp25 || ''}
                    onChange={(e) => updateStudentField(student.id, 'date_of_initial_block_term_verified_sp25', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                {/* Field 11: Matriculation Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    11. Matriculation Date College Start Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={studentData[student.id]?.matriculation_date_verified_sp25 || ''}
                    onChange={(e) => updateStudentField(student.id, 'matriculation_date_verified_sp25', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                {/* Field 12: Conferred Graduation Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    12. Conferred Graduation Date (if graduated)
                  </label>
                  <input
                    type="date"
                    value={studentData[student.id]?.conferred_grad_date_verified_sp25 || ''}
                    onChange={(e) => updateStudentField(student.id, 'conferred_grad_date_verified_sp25', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Field 13: Final Cumulative GPA (conditional) */}
                {studentData[student.id]?.conferred_grad_date_verified_sp25 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      13. Final Cumulative GPA (Graduation/Degree Completion) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="4.0"
                      value={studentData[student.id]?.cumulative_gpa_at_graduation_verified_sp25 || ''}
                      onChange={(e) => updateStudentField(student.id, 'cumulative_gpa_at_graduation_verified_sp25', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                )}

                {/* Field 14: Current Cumulative GPA */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    14. Current Cumulative GPA (Spring 2025) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={studentData[student.id]?.gpa_verified || ''}
                    onChange={(e) => updateStudentField(student.id, 'gpa_verified', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., 3.75"
                    required
                  />
                </div>

                {/* Field 15: Expected Graduation Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    15. Expected Graduation Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={studentData[student.id]?.expected_grad_date_verified_sp25 || ''}
                    onChange={(e) => updateStudentField(student.id, 'expected_grad_date_verified_sp25', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                {/* Field 16: Entering Major at Matriculation */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    16. Entering Major at Matriculation <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={studentData[student.id]?.major_at_matriculation_verified_sp25 || ''}
                    onChange={(e) => updateStudentField(student.id, 'major_at_matriculation_verified_sp25', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                {/* Field 17: Current Major */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    17. Current Major or Major of Completed Degree (Spring 2025) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={studentData[student.id]?.major_verified || ''}
                    onChange={(e) => updateStudentField(student.id, 'major_verified', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                {/* Field 18: Classification */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    18. Classification (Fall 2025) <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={studentData[student.id]?.current_academic_class_verified || ''}
                    onChange={(e) => updateStudentField(student.id, 'current_academic_class_verified', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select...</option>
                    {classificationOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>

                {/* Field 19: Citizenship */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    19. Citizenship <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={studentData[student.id]?.citizenship_status_verified_sp25 || ''}
                    onChange={(e) => updateStudentField(student.id, 'citizenship_status_verified_sp25', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select...</option>
                    {citizenshipOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>

                {/* Field 20: Gender */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    20. Gender <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={studentData[student.id]?.gender_verified || ''}
                    onChange={(e) => updateStudentField(student.id, 'gender_verified', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select...</option>
                    {genderOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>

                {/* Field 21: Race - Only show if required */}
                {raceRequired && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      21. Race <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={studentData[student.id]?.race_verified || ''}
                      onChange={(e) => updateStudentField(student.id, 'race_verified', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">Select...</option>
                      {raceOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Submit Button */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-600">
                {(() => {
                  const unverifiedCount = verificationData.students.filter(s => !s.existing_verification.is_verified).length;
                  const verifiedCount = verificationData.students.length - unverifiedCount;
                  return (
                    <span>
                      {verifiedCount > 0 && `${verifiedCount} student(s) already verified. `}
                      {unverifiedCount > 0 ? `${unverifiedCount} student(s) ready to submit.` : 'All students verified!'}
                    </span>
                  );
                })()}
              </div>
              <button
                type="submit"
                disabled={submitting || verificationData.students.every(s => s.existing_verification.is_verified)}
                className={`px-8 py-3 rounded-md font-medium transition-colors ${
                  submitting || verificationData.students.every(s => s.existing_verification.is_verified)
                    ? 'bg-gray-400 text-gray-700 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {submitting ? 'Submitting...' : 
                 verificationData.students.every(s => s.existing_verification.is_verified) ? 'All Students Verified' :
                 'Submit Verification'}
              </button>
            </div>
          </div>
          </form>
        )}

        {/* Paginated View */}
        {viewMode === 'paginated' && filteredStudents.length > 0 && (
          <>
            {/* Student Navigation */}
            <div className="bg-white rounded-lg shadow-lg p-4 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Student {currentStudentIndex + 1} of {filteredStudents.length}
                  {searchQuery && ` (filtered from ${verificationData.students.length} total)`}
                </h3>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={prevStudent}
                    disabled={currentStudentIndex === 0}
                    className={`px-3 py-1 rounded text-sm font-medium ${
                      currentStudentIndex === 0
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                    }`}
                  >
                    ← Previous
                  </button>
                  <button
                    type="button"
                    onClick={nextStudent}
                    disabled={currentStudentIndex === filteredStudents.length - 1}
                    className={`px-3 py-1 rounded text-sm font-medium ${
                      currentStudentIndex === filteredStudents.length - 1
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                    }`}
                  >
                    Next →
                  </button>
                </div>
              </div>
              
              {/* Student List */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {filteredStudents.map((student, index) => (
                  <button
                    key={student.id}
                    type="button"
                    onClick={() => goToStudent(index)}
                    className={`p-2 text-left text-xs rounded border ${
                      index === currentStudentIndex
                        ? 'bg-blue-100 border-blue-500 text-blue-900'
                        : student.existing_verification.is_verified
                        ? 'bg-green-100 border-green-500 text-green-900'
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-1">
                      {/* Priority flag */}
                      {student.created_at !== 'default' && student.partner_has_verified_status === 'pending' && (
                        <svg className="w-3 h-3 text-red-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      )}
                      {/* Verified checkmark */}
                      {student.existing_verification.is_verified && (
                        <svg className="w-3 h-3 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                      <span className="truncate">{student.name}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Single Student Form */}
            <form onSubmit={handleSingleStudentSubmit}>
              {(() => {
                const student = filteredStudents[currentStudentIndex];
                return (
                  <div key={student.id} className={`rounded-lg shadow-lg p-6 mb-6 ${
                    student.existing_verification.is_verified 
                      ? 'bg-green-50 border-2 border-green-200' 
                      : 'bg-white'
                  }`}>
                    <div className="flex justify-between items-start mb-6">
                      <div className="flex items-center">
                        {/* Priority flag */}
                        {student.created_at !== 'default' && student.partner_has_verified_status === 'pending' && (
                          <svg className="w-5 h-5 text-red-600 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        )}
                        <h2 className="text-xl font-semibold text-gray-900">
                          {student.name}
                        </h2>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        {/* Priority application info */}
                        {student.created_at !== 'default' && student.partner_has_verified_status === 'pending' && (
                          <div className="text-right">
                            <p className="text-sm font-medium text-red-600">Applied for NACME Corporate Scholarship:</p>
                            <p className="text-sm text-red-500">{new Date(student.created_at).toLocaleDateString()}</p>
                          </div>
                        )}
                        {/* Verified status */}
                        {student.existing_verification.is_verified && (
                          <div className="flex items-center text-green-600">
                            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            <span className="font-medium">Verified</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {student.existing_verification.is_verified && (
                      <div className="mb-4 p-3 bg-green-100 border border-green-300 rounded">
                        <p className="text-green-800 text-sm">
                          ✓ This student has already been verified.
                        </p>
                      </div>
                    )}
                    
                    <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 ${
                      student.existing_verification.is_verified ? 'opacity-50 pointer-events-none' : ''
                    }`}>
                      {/* Field 1: Name (read-only) */}
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          1. Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={student.name}
                          disabled
                          className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600"
                        />
                      </div>

                      {/* Field 2: First Name */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          2. First Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={studentData[student.id]?.first_name_verified_sp25 || ''}
                          onChange={(e) => updateStudentField(student.id, 'first_name_verified_sp25', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>

                      {/* Field 3: Last Name */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          3. Last Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={studentData[student.id]?.last_name_verified_sp25 || ''}
                          onChange={(e) => updateStudentField(student.id, 'last_name_verified_sp25', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>

                      {/* Field 4: NACME Block Grant Scholarship */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          4. NACME Block Grant Scholarship Recipient? <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={studentData[student.id]?.block_grant_scholar_verified_sp25 || ''}
                          onChange={(e) => updateStudentField(student.id, 'block_grant_scholar_verified_sp25', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        >
                          <option value="">Select...</option>
                          <option value="Y">Y</option>
                          <option value="N">N</option>
                        </select>
                      </div>

                      {/* Field 5: NACME Corporate Scholarship */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          5. NACME Corporate Scholarship Recipient? <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={studentData[student.id]?.corporate_scholar_verified_sp25 || ''}
                          onChange={(e) => updateStudentField(student.id, 'corporate_scholar_verified_sp25', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        >
                          <option value="">Select...</option>
                          <option value="Y">Y</option>
                          <option value="N">N</option>
                        </select>
                      </div>

                      {/* Field 6: NACME Special Scholarship */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          6. NACME Special Scholarship Recipient? <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={studentData[student.id]?.special_scholarship_verified_sp25 || ''}
                          onChange={(e) => updateStudentField(student.id, 'special_scholarship_verified_sp25', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        >
                          <option value="">Select...</option>
                          <option value="Y">Y</option>
                          <option value="N">N</option>
                        </select>
                      </div>

                      {/* Field 7: NACME NVB Scholarship */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          7. NACME National Virtual Bridge (NVB) Scholarship Recipient? <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={studentData[student.id]?.nvb_scholar_verified_sp25 || ''}
                          onChange={(e) => updateStudentField(student.id, 'nvb_scholar_verified_sp25', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        >
                          <option value="">Select...</option>
                          <option value="Y">Y</option>
                          <option value="N">N</option>
                        </select>
                      </div>

                      {/* Field 8: University Email */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          8. Enter University Email Address <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          value={studentData[student.id]?.university_email_verified || ''}
                          onChange={(e) => updateStudentField(student.id, 'university_email_verified', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>

                      {/* Field 9: Scholar Status */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          9. Scholar Status (Fall 2025) <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={studentData[student.id]?.applicant_status_verified_sp25 || ''}
                          onChange={(e) => updateStudentField(student.id, 'applicant_status_verified_sp25', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        >
                          <option value="">Select...</option>
                          {scholarStatusOptions.map(option => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      </div>

                      {/* Field 10: Date of Initial NACME Block Scholarship */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          10. Date/Term of Initial NACME Block Scholarship <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="date"
                          value={studentData[student.id]?.date_of_initial_block_term_verified_sp25 || ''}
                          onChange={(e) => updateStudentField(student.id, 'date_of_initial_block_term_verified_sp25', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>

                      {/* Field 11: Matriculation Date */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          11. Matriculation Date College Start Date <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="date"
                          value={studentData[student.id]?.matriculation_date_verified_sp25 || ''}
                          onChange={(e) => updateStudentField(student.id, 'matriculation_date_verified_sp25', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>

                      {/* Field 12: Conferred Graduation Date */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          12. Conferred Graduation Date (if graduated)
                        </label>
                        <input
                          type="date"
                          value={studentData[student.id]?.conferred_grad_date_verified_sp25 || ''}
                          onChange={(e) => updateStudentField(student.id, 'conferred_grad_date_verified_sp25', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      {/* Field 13: Final Cumulative GPA (conditional) */}
                      {studentData[student.id]?.conferred_grad_date_verified_sp25 && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            13. Final Cumulative GPA (Graduation/Degree Completion) <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            max="4.0"
                            value={studentData[student.id]?.cumulative_gpa_at_graduation_verified_sp25 || ''}
                            onChange={(e) => updateStudentField(student.id, 'cumulative_gpa_at_graduation_verified_sp25', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                          />
                        </div>
                      )}

                      {/* Field 14: Current Cumulative GPA */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          14. Current Cumulative GPA (Spring 2025) <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={studentData[student.id]?.gpa_verified || ''}
                          onChange={(e) => updateStudentField(student.id, 'gpa_verified', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., 3.75"
                          required
                        />
                      </div>

                      {/* Field 15: Expected Graduation Date */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          15. Expected Graduation Date <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="date"
                          value={studentData[student.id]?.expected_grad_date_verified_sp25 || ''}
                          onChange={(e) => updateStudentField(student.id, 'expected_grad_date_verified_sp25', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>

                      {/* Field 16: Entering Major at Matriculation */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          16. Entering Major at Matriculation <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={studentData[student.id]?.major_at_matriculation_verified_sp25 || ''}
                          onChange={(e) => updateStudentField(student.id, 'major_at_matriculation_verified_sp25', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>

                      {/* Field 17: Current Major */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          17. Current Major or Major of Completed Degree (Spring 2025) <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={studentData[student.id]?.major_verified || ''}
                          onChange={(e) => updateStudentField(student.id, 'major_verified', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>

                      {/* Field 18: Classification */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          18. Classification (Fall 2025) <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={studentData[student.id]?.current_academic_class_verified || ''}
                          onChange={(e) => updateStudentField(student.id, 'current_academic_class_verified', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        >
                          <option value="">Select...</option>
                          {classificationOptions.map(option => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      </div>

                      {/* Field 19: Citizenship */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          19. Citizenship <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={studentData[student.id]?.citizenship_status_verified_sp25 || ''}
                          onChange={(e) => updateStudentField(student.id, 'citizenship_status_verified_sp25', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        >
                          <option value="">Select...</option>
                          {citizenshipOptions.map(option => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      </div>

                      {/* Field 20: Gender */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          20. Gender <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={studentData[student.id]?.gender_verified || ''}
                          onChange={(e) => updateStudentField(student.id, 'gender_verified', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        >
                          <option value="">Select...</option>
                          {genderOptions.map(option => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      </div>

                      {/* Field 21: Race - Only show if required */}
                      {raceRequired && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            21. Race <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={studentData[student.id]?.race_verified || ''}
                            onChange={(e) => updateStudentField(student.id, 'race_verified', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                          >
                            <option value="">Select...</option>
                            {raceOptions.map(option => (
                              <option key={option} value={option}>{option}</option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>

                    {/* Submit Button for Single Student */}
                    <div className="bg-white rounded-lg shadow-lg p-6">
                      <div className="flex justify-between items-center">
                        <div className="text-sm text-gray-600">
                          Student {currentStudentIndex + 1} of {verificationData.students.length}
                          {student.existing_verification.is_verified && (
                            <span className="ml-2 text-green-600">✓ Already verified</span>
                          )}
                        </div>
                        <div className="flex gap-2">
                          {currentStudentIndex < verificationData.students.length - 1 && (
                            <button
                              type="button"
                              onClick={nextStudent}
                              className="px-4 py-2 bg-gray-600 text-white text-sm rounded hover:bg-gray-700 transition-colors"
                            >
                              Skip to Next
                            </button>
                          )}
                          <button
                            type="submit"
                            disabled={submitting || student.existing_verification.is_verified}
                            className={`px-8 py-3 rounded-md font-medium transition-colors ${
                              submitting || student.existing_verification.is_verified
                                ? 'bg-gray-400 text-gray-700 cursor-not-allowed'
                                : 'bg-blue-600 text-white hover:bg-blue-700'
                            }`}
                          >
                            {submitting ? 'Submitting...' : 
                             student.existing_verification.is_verified ? 'Already Verified' :
                             'Submit This Student'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default PartnerVerificationForm;